import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { GoogleAuthProvider, onAuthStateChanged, signInWithPopup, signOut, type User } from 'firebase/auth';
import { getFirebase } from './firebase.js';

const DEV_AUTH = import.meta.env.VITE_DEV_AUTH === 'true';
const LS_KEY = 'tv_dev_user_email';

interface DevUser {
  email: string;
  displayName: string | null;
  uid: string;
  getIdToken(): Promise<string>;
}

interface AuthState {
  user: User | DevUser | null;
  loading: boolean;
  signIn: (email?: string) => Promise<void>;
  signOutNow: () => Promise<void>;
  getToken: () => Promise<string | null>;
  isDev: boolean;
}

const Ctx = createContext<AuthState | undefined>(undefined);

function makeDevUser(email: string): DevUser {
  return {
    email,
    displayName: email.split('@')[0],
    uid: `dev_${email.replace(/[^a-z0-9]/gi, '_')}`,
    async getIdToken() { return `dev-${email}`; },
  };
}

// Initialize Firebase eagerly at module load (not inside the click handler).
// This way, signInWithPopup is called inside the click handler with no awaits in front of it,
// preserving Chrome's "user gesture" detection so the popup isn't blocked.
let firebaseInitFailed: string | null = null;
try {
  if (!DEV_AUTH) getFirebase();
} catch (err) {
  firebaseInitFailed = err instanceof Error ? err.message : String(err);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | DevUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (DEV_AUTH) {
      const saved = localStorage.getItem(LS_KEY);
      if (saved) setUser(makeDevUser(saved));
      setLoading(false);
      return;
    }
    if (firebaseInitFailed) { setLoading(false); return; }
    let unsub: (() => void) | undefined;
    try {
      const { auth } = getFirebase();
      unsub = onAuthStateChanged(auth, u => { setUser(u); setLoading(false); });
    } catch (err) {
      console.error('Firebase auth state listener failed:', err);
      setLoading(false);
    }
    return () => unsub?.();
  }, []);

  // Dev mode: prompt + local user.
  // Prod: must be called synchronously from a click handler with no awaits in front of it.
  const signIn = async (email?: string) => {
    if (DEV_AUTH) {
      const e = (email ?? prompt('Sign in as (email):') ?? '').trim().toLowerCase();
      if (!e || !e.includes('@')) return;
      localStorage.setItem(LS_KEY, e);
      setUser(makeDevUser(e));
      return;
    }
    if (firebaseInitFailed) {
      alert(`Firebase config missing: ${firebaseInitFailed}\n\nAsk the deploy to set VITE_FIREBASE_* env vars and redeploy.`);
      return;
    }
    const { auth } = getFirebase();
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
    } catch (err: any) {
      const code = err?.code ?? '';
      console.error('signInWithPopup failed:', code, err);
      if (code === 'auth/popup-blocked' || code === 'auth/cancelled-popup-request') {
        alert('Your browser blocked the sign-in popup. Allow popups for this site and try again.');
      } else if (code === 'auth/unauthorized-domain') {
        alert('This domain is not authorized for sign-in. Add it under Firebase Console → Authentication → Settings → Authorized domains.');
      } else if (code === 'auth/popup-closed-by-user') {
        // user dismissed; no-op
      } else {
        alert(`Sign-in failed: ${err?.message ?? String(err)}`);
      }
    }
  };

  const signOutNow = async () => {
    if (DEV_AUTH) { localStorage.removeItem(LS_KEY); setUser(null); return; }
    const { auth } = getFirebase();
    await signOut(auth);
  };

  const getToken = async () => {
    if (!user) return null;
    return user.getIdToken();
  };

  return <Ctx.Provider value={{ user, loading, signIn, signOutNow, getToken, isDev: DEV_AUTH }}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth must be inside AuthProvider');
  return v;
}
